import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ICycleRequest, ICycleSummary } from '../../interfaces/appraisal.interface';
import { cycleStatusColor } from './bands';
import { formatDate } from './dates';
import CycleDetailsForm, { emptyCycleRequest, validateCycleRequest } from './CycleDetailsForm';
import ConfirmDialog from './ConfirmDialog';

/** Admin list of appraisal cycles, newest first, with a dialog to start a new one. */
const CycleList: React.FC = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation(['appraisal', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [cycles, setCycles] = useState<ICycleSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState<ICycleRequest>(emptyCycleRequest);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<ICycleSummary | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            setCycles(await AppraisalService.listCycles(institutionId));
        } catch (err) {
            toast.showApiError(err, t('errors.loadCycles'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const create = async () => {
        const error = validateCycleRequest(form);
        if (error) {
            toast.showError(t(`cycleForm.errors.${error}`));
            return;
        }
        setSaving(true);
        try {
            const created = await AppraisalService.createCycle(institutionId, { ...form, name: form.name.trim() });
            setCreateOpen(false);
            navigate(`/appraisals/cycles/${created.id}`);
        } catch (err) {
            toast.showApiError(err, t('cycleForm.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!deleting) return;
        setSaving(true);
        try {
            await AppraisalService.deleteCycle(institutionId, deleting.id);
            toast.showSuccess(t('editor.deleted'));
            setDeleting(null);
            load();
        } catch (err) {
            toast.showApiError(err, t('editor.errors.delete'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">{t('cycleList.title')}</Typography>
                <Button variant="contained" onClick={() => { setForm(emptyCycleRequest); setCreateOpen(true); }}>
                    {t('cycleList.newCycle')}
                </Button>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('columns.name')}</TableCell>
                            <TableCell>{t('columns.period')}</TableCell>
                            <TableCell>{t('columns.selfDue')}</TableCell>
                            <TableCell>{t('columns.managerDue')}</TableCell>
                            <TableCell>{t('columns.status')}</TableCell>
                            <TableCell>{t('columns.progress')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7}><Typography>{t('loading')}</Typography></TableCell></TableRow>
                        ) : cycles.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><Typography color="text.secondary">{t('cycleList.empty')}</Typography></TableCell></TableRow>
                        ) : cycles.map(c => (
                            <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/appraisals/cycles/${c.id}`)}>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{t('editor.period', { from: formatDate(c.periodStart), to: formatDate(c.periodEnd) })}</TableCell>
                                <TableCell>{formatDate(c.selfReviewDue)}</TableCell>
                                <TableCell>{formatDate(c.managerReviewDue)}</TableCell>
                                <TableCell><Chip size="small" label={t(`cycleStatus.${c.status}`)} color={cycleStatusColor[c.status]} /></TableCell>
                                <TableCell>{t('cycleList.progress', { completed: c.completedCount, total: c.participantCount })}</TableCell>
                                <TableCell align="right" onClick={e => e.stopPropagation()}>
                                    {c.status === 'DRAFT' && (
                                        <Tooltip title={t('common:actions.delete')}>
                                            <IconButton color="error" onClick={() => setDeleting(c)} aria-label={t('common:actions.delete')}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={createOpen} onClose={saving ? undefined : () => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('cycleForm.newTitle')}</DialogTitle>
                <DialogContent>
                    <CycleDetailsForm value={form} onChange={setForm} disabled={saving} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={create} disabled={saving}>{t('common:actions.create')}</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!deleting}
                title={t('editor.confirmDelete.title')}
                body={t('editor.confirmDelete.body', { name: deleting?.name ?? '' })}
                confirmLabel={t('common:actions.delete')}
                danger
                busy={saving}
                onConfirm={remove}
                onClose={() => setDeleting(null)}
            />
        </Box>
    );
};

export default CycleList;
